/**
 * Slate-based email editor with toolbar and HTML value/onChange interface.
 * Use in Compose and ManageSignaturesModal; value is HTML, onChange(html).
 * Optional ref exposes insertImage(url) for external image insert (e.g. modal).
 */
import React, { useCallback, useEffect, useImperativeHandle, useMemo, forwardRef, useState, useRef } from 'react';
import { createEditor, Descendant, Editor, Element as SlateElement, Node, Range, Transforms } from 'slate';
import type { Path } from 'slate';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import type { CustomElement, CustomText } from './slate-types';
import { htmlToSlate, slateToHtml } from './slate-html';
import { coalescePlainTextParagraphBlocks } from '../../modules/emails/utils/unlockReplyForwardEditing';
import { insertEmailImage, normalizeEditorSelection } from './slate-editor-utils';
import SlateEmailToolbar from './SlateEmailToolbar';
import { QuotedRichHtmlElement } from './QuotedRichHtmlElement';
import { cn } from '../../lib/utils';
import DOMPurify from 'dompurify';
import { sanitizeRichEmailHtml } from '../../modules/emails/utils/sanitizeEmailBody';
import { EMAIL_COMPOSE_EDITOR_CANVAS_CLASS } from '../../modules/emails/utils/emailMessageCanvas';
import {
  THUNDERBIRD_ADDRESS_STYLE,
  THUNDERBIRD_BODY_TEXT_STYLE,
  THUNDERBIRD_HEADING_STYLES,
  THUNDERBIRD_PARAGRAPH_STYLE,
  THUNDERBIRD_PREFORMAT_STYLE,
} from '../../modules/emails/utils/thunderbirdParagraphStyles';

/** Preserve bordered reply/forward header box (server-generated) after sanitize. */
const REPLY_HEADER_PURIFY: Parameters<typeof DOMPurify.sanitize>[1] = {
  ADD_ATTR: ['style', 'class'],
};

function inlineStyleStringToReact(styleStr: string | undefined): React.CSSProperties {
  if (!styleStr?.trim()) return {};
  const o: Record<string, string> = {};
  for (const part of styleStr.split(';')) {
    const colon = part.indexOf(':');
    if (colon === -1) continue;
    const rawKey = part.slice(0, colon).trim();
    const val = part.slice(colon + 1).trim();
    if (!rawKey || !val) continue;
    const camel = rawKey.replace(/-([a-z])/gi, (_, c: string) => c.toUpperCase());
    o[camel] = val;
  }
  return o as React.CSSProperties;
}

const MIN_TABLE_WIDTH_PX = 80;
const MAX_TABLE_WIDTH_PX = 2000;
const MIN_TABLE_HEIGHT_PX = 24;
const MAX_TABLE_HEIGHT_PX = 1200;
const MIN_ROW_HEIGHT_PX = 20;
const MAX_ROW_HEIGHT_PX = 400;
const MIN_COLUMN_WIDTH_PX = 32;
const MAX_COLUMN_WIDTH_PX = 800;
const MIN_IMAGE_SIZE_PX = 24;
const MAX_IMAGE_SIZE_PX = 1200;

/** Same document structure as EmailBodyView so forwarded content renders identically to email view. */
function QuotedEmailIframe({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const srcdoc = useMemo(() => {
    const safeHtml = sanitizeRichEmailHtml(html);
    if (!safeHtml) return '';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank" rel="noopener noreferrer"><style>img{max-width:100%;height:auto}img[width][height]{max-width:100%;height:auto}</style></head><body style="margin:0;padding:8px;background:#ffffff;color:#111827">${safeHtml}</body></html>`;
  }, [html]);
  useEffect(() => {
    if (!iframeRef.current || !html) return;
    const iframe = iframeRef.current;
    const timeoutIds: number[] = [];
    const measure = () => {
      try {
        const doc = iframe.contentDocument;
        if (doc?.body) setHeight(Math.max(120, doc.documentElement.scrollHeight));
      } catch {
        // sandbox may block access
      }
    };
    const onLoad = () => {
      measure();
      timeoutIds.push(window.setTimeout(measure, 400));
      timeoutIds.push(window.setTimeout(measure, 1200));
    };
    iframe.addEventListener('load', onLoad);
    return () => {
      iframe.removeEventListener('load', onLoad);
      timeoutIds.forEach(clearTimeout);
    };
  }, [html]);
  if (!html) return null;
  return (
    <iframe
      ref={iframeRef}
      title="Quoted email"
      srcDoc={srcdoc}
      sandbox="allow-same-origin"
      style={{
        width: '100%',
        height,
        minHeight: 120,
        border: 'none',
        display: 'block',
        overflow: 'hidden',
      }}
    />
  );
}

export interface SlateEmailEditorRef {
  insertImage: (url: string) => void;
  insertHtml: (html: string) => void;
  /** Current selection as plain text; empty string if none or collapsed. */
  getSelectedText: () => string;
  /** Current editor content as HTML (from Slate state, so always in sync at call time). */
  getHtml: () => string;
}

export interface SlateEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  contentKey?: number | string;
  toolbarConfig?: Parameters<typeof SlateEmailToolbar>[0]['config'];
  onInsertImage?: () => void;
  className?: string;
  minHeight?: number | string;
  /** When true, editor fills container height (flex-1); use in compose for full-height message area */
  fillHeight?: boolean;
  /** Keep formatting toolbar visible while the message area scrolls (compose). */
  stickyToolbar?: boolean;
  /** Rendered below the editable area inside the scroll region (e.g. compose signature). */
  belowEditor?: React.ReactNode;
  /** Rendered after belowEditor inside the scroll region (e.g. quoted original). */
  scrollFooter?: React.ReactNode;
  /** Reply/forward: shrink the typing area to content (no tall min-height gap before signature). */
  compactUserZone?: boolean;
  /** Outlook reply coalesce drops inline images; disable for signatures and other rich HTML. */
  coalescePlainTextBlocks?: boolean;
  colors?: Record<string, string>;
}

function useEditorWithPlugins() {
  return useMemo(() => {
    const base = createEditor();
    const withVoidAndInline = (e: typeof base) => {
      const { isVoid, isInline } = e;
      e.isVoid = (n) =>
        (SlateElement.isElement(n) &&
          ((n as CustomElement).type === 'image' ||
            (n as CustomElement).type === 'raw-html' ||
            (n as CustomElement).type === 'quoted-rich-html')) ||
        isVoid(n);
      e.isInline = (n) => (SlateElement.isElement(n) && (n as CustomElement).type === 'image') || isInline(n);
      return e;
    };
    return withHistory(withReact(withVoidAndInline(base)));
  }, []);
}

function slateNodesFromHtml(html: string, coalescePlainTextBlocks: boolean): Descendant[] {
  const blocks = htmlToSlate(html);
  if (!coalescePlainTextBlocks) return blocks;
  return coalescePlainTextParagraphBlocks(blocks) ?? blocks;
}

function useInitialValue(
  html: string,
  resetKey: number | string | undefined,
  coalescePlainTextBlocks: boolean,
): Descendant[] {
  // html is read when resetKey changes (parent remounts via key={contentKey}).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => slateNodesFromHtml(html, coalescePlainTextBlocks), [resetKey, coalescePlainTextBlocks]);
}

function loadHtmlIntoEditor(editor: Editor, html: string, coalescePlainTextBlocks: boolean): void {
  const next = slateNodesFromHtml(html, coalescePlainTextBlocks);
  Editor.withoutNormalizing(editor, () => {
    editor.children = next;
  });
  Editor.normalize(editor, { force: true });
  normalizeEditorSelection(editor);
}

const SlateEmailEditor = forwardRef<SlateEmailEditorRef, SlateEmailEditorProps>(function SlateEmailEditor({
  value,
  onChange,
  placeholder = 'Compose your message...',
  contentKey,
  toolbarConfig,
  onInsertImage,
  className,
  minHeight = 300,
  fillHeight = false,
  stickyToolbar = false,
  belowEditor,
  scrollFooter,
  compactUserZone = false,
  coalescePlainTextBlocks = false,
  colors = {},
}, ref) {
  const editor = useEditorWithPlugins();
  const editableHostRef = useRef<HTMLDivElement>(null);
  const initialValue = useInitialValue(value, contentKey, coalescePlainTextBlocks);

  // Reload document when contentKey changes (edit load, HTML → Visual) without resetting on every keystroke.
  useEffect(() => {
    loadHtmlIntoEditor(editor, value, coalescePlainTextBlocks);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value is intentionally read only when contentKey changes
  }, [contentKey, editor, coalescePlainTextBlocks]);
  const [tableResize, setTableResize] = useState<{
    path: Path;
    startX: number;
    startY: number;
    startWidthPx: number;
    startHeightPx: number;
  } | null>(null);

  const startTableResize = useCallback(
    (path: Path, clientX: number, clientY: number, currentWidthPx: number, currentHeightPx: number) => {
      setTableResize({ path, startX: clientX, startY: clientY, startWidthPx: currentWidthPx, startHeightPx: currentHeightPx });
    },
    []
  );

  useEffect(() => {
    if (!tableResize) return;
    const onMove = (e: MouseEvent) => {
      const deltaX = e.clientX - tableResize.startX;
      const deltaY = e.clientY - tableResize.startY;
      let w = tableResize.startWidthPx + deltaX;
      let h = tableResize.startHeightPx + deltaY;
      w = Math.max(MIN_TABLE_WIDTH_PX, Math.min(MAX_TABLE_WIDTH_PX, w));
      h = Math.max(MIN_TABLE_HEIGHT_PX, Math.min(MAX_TABLE_HEIGHT_PX, h));
      const updates: { width?: string; height?: string } = { width: `${Math.round(w)}px`, height: `${Math.round(h)}px` };
      Transforms.setNodes(editor, updates, { at: tableResize.path });
    };
    const onUp = () => setTableResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [editor, tableResize]);

  const [rowResize, setRowResize] = useState<{ path: Path; startY: number; startHeightPx: number } | null>(null);
  const startRowResize = useCallback((path: Path, clientY: number, currentHeightPx: number) => {
    setRowResize({ path, startY: clientY, startHeightPx: currentHeightPx });
  }, []);

  const [imageResize, setImageResize] = useState<{
    path: Path;
    startX: number;
    startY: number;
    startWidthPx: number;
    startHeightPx: number;
  } | null>(null);
  const startImageResize = useCallback(
    (path: Path, clientX: number, clientY: number, currentWidthPx: number, currentHeightPx: number) => {
      setImageResize({ path, startX: clientX, startY: clientY, startWidthPx: currentWidthPx, startHeightPx: currentHeightPx });
    },
    []
  );

  useEffect(() => {
    if (!imageResize) return;
    const onMove = (e: MouseEvent) => {
      const deltaX = e.clientX - imageResize.startX;
      const deltaY = e.clientY - imageResize.startY;
      let w = imageResize.startWidthPx + deltaX;
      let h = imageResize.startHeightPx + deltaY;
      w = Math.max(MIN_IMAGE_SIZE_PX, Math.min(MAX_IMAGE_SIZE_PX, w));
      h = Math.max(MIN_IMAGE_SIZE_PX, Math.min(MAX_IMAGE_SIZE_PX, h));
      Transforms.setNodes(editor, { width: `${Math.round(w)}px`, height: `${Math.round(h)}px` }, { at: imageResize.path });
    };
    const onUp = () => setImageResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [editor, imageResize]);

  useEffect(() => {
    if (!rowResize) return;
    const onMove = (e: MouseEvent) => {
      const deltaY = e.clientY - rowResize.startY;
      let h = rowResize.startHeightPx + deltaY;
      h = Math.max(MIN_ROW_HEIGHT_PX, Math.min(MAX_ROW_HEIGHT_PX, h));
      Transforms.setNodes(editor, { height: `${Math.round(h)}px` }, { at: rowResize.path });
    };
    const onUp = () => setRowResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [editor, rowResize]);

  const [columnResize, setColumnResize] = useState<{
    tablePath: Path;
    columnIndex: number;
    startX: number;
    startWidthPx: number;
  } | null>(null);
  const startColumnResize = useCallback(
    (tablePath: Path, columnIndex: number, clientX: number, startWidthPx: number) => {
      setColumnResize({ tablePath, columnIndex, startX: clientX, startWidthPx });
    },
    []
  );

  useEffect(() => {
    if (!columnResize) return;
    const onMove = (e: MouseEvent) => {
      const deltaX = e.clientX - columnResize.startX;
      let w = columnResize.startWidthPx + deltaX;
      w = Math.max(MIN_COLUMN_WIDTH_PX, Math.min(MAX_COLUMN_WIDTH_PX, w));
      const widthStr = `${Math.round(w)}px`;
      let table: CustomElement;
      try {
        table = Node.get(editor, columnResize.tablePath) as CustomElement;
      } catch {
        setColumnResize(null);
        return;
      }
      if (!SlateElement.isElement(table) || table.type !== 'table') {
        setColumnResize(null);
        return;
      }
      const rows = table.children as CustomElement[];
      Editor.withoutNormalizing(editor, () => {
        for (let r = 0; r < rows.length; r++) {
          const rowPath = [...columnResize.tablePath, r];
          let row: CustomElement;
          try {
            row = Node.get(editor, rowPath) as CustomElement;
          } catch {
            continue;
          }
          if (!SlateElement.isElement(row) || row.type !== 'table-row') continue;
          if (columnResize.columnIndex >= row.children.length) continue;
          Transforms.setNodes(editor, { width: widthStr } as Partial<CustomElement>, {
            at: [...rowPath, columnResize.columnIndex],
          });
        }
      });
    };
    const onUp = () => setColumnResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [editor, columnResize]);

  const syncHtmlToParent = useCallback(() => {
    normalizeEditorSelection(editor);
    onChange(slateToHtml(editor.children));
  }, [editor, onChange]);

  useImperativeHandle(ref, () => ({
    insertImage(url: string) {
      insertEmailImage(editor, url);
      syncHtmlToParent();
    },
    insertHtml(html: string) {
      if (!html?.trim()) return;
      const nodes = htmlToSlate(html);
      if (nodes.length) Transforms.insertNodes(editor, nodes);
    },
    getSelectedText(): string {
      const { selection } = editor;
      if (selection && !Range.isCollapsed(selection)) {
        try {
          const s = Editor.string(editor, selection);
          if (s.length > 0) return s;
        } catch {
          /**/
        }
      }
      // Fallback when Slate selection is empty but DOM still has a range (e.g. across some void nodes)
      const domSel = typeof window !== 'undefined' ? window.getSelection() : null;
      if (!domSel || domSel.rangeCount === 0 || domSel.isCollapsed) return '';
      const host = editableHostRef.current;
      const node = domSel.anchorNode;
      if (!host || !node || !host.contains(node)) return '';
      return (domSel.toString() || '').trim();
    },
    getHtml(): string {
      try {
        return slateToHtml(editor.children);
      } catch {
        return '';
      }
    },
  }), [editor, syncHtmlToParent]);

  const handleChange = useCallback(
    (newValue: Descendant[]) => {
      normalizeEditorSelection(editor);
      onChange(slateToHtml(newValue));
    },
    [editor, onChange]
  );

  const handlePaste = useCallback((event: React.ClipboardEvent) => {
    const e = event as React.ClipboardEvent & Pick<KeyboardEvent, 'ctrlKey' | 'metaKey' | 'shiftKey'>;
    const clipboardData = event.clipboardData;
    if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
      event.preventDefault();
      const text = clipboardData?.getData('text/plain') ?? '';
      if (text) Transforms.insertText(editor, text);
      return;
    }
    const file = clipboardData?.items ? Array.from(clipboardData.items).find((item) => item.kind === 'file' && item.type.startsWith('image/')) : null;
    if (file) {
      const blob = file.getAsFile();
      if (blob) {
        event.preventDefault();
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          insertEmailImage(editor, dataUrl);
          queueMicrotask(() => syncHtmlToParent());
        };
        reader.readAsDataURL(blob);
      }
    }
  }, [editor, syncHtmlToParent]);

  const renderElement = useCallback((props: { attributes: any; children: any; element: CustomElement }) => {
    const { attributes, children, element } = props;
    const tableWidth = element.type === 'table' ? (element as { width?: string }).width : undefined;
    const tableHeight = element.type === 'table' ? (element as { height?: string }).height : undefined;
    switch (element.type) {
      case 'body-text':
        return (
          <div
            {...attributes}
            style={{ ...THUNDERBIRD_BODY_TEXT_STYLE, textAlign: element.align }}
          >
            {children}
          </div>
        );
      case 'heading-one':
        return (
          <h1 {...attributes} style={{ ...THUNDERBIRD_HEADING_STYLES['heading-one'], textAlign: element.align }}>
            {children}
          </h1>
        );
      case 'heading-two':
        return (
          <h2 {...attributes} style={{ ...THUNDERBIRD_HEADING_STYLES['heading-two'], textAlign: element.align }}>
            {children}
          </h2>
        );
      case 'heading-three':
        return (
          <h3 {...attributes} style={{ ...THUNDERBIRD_HEADING_STYLES['heading-three'], textAlign: element.align }}>
            {children}
          </h3>
        );
      case 'heading-four':
        return (
          <h4 {...attributes} style={{ ...THUNDERBIRD_HEADING_STYLES['heading-four'], textAlign: element.align }}>
            {children}
          </h4>
        );
      case 'heading-five':
        return (
          <h5 {...attributes} style={{ ...THUNDERBIRD_HEADING_STYLES['heading-five'], textAlign: element.align }}>
            {children}
          </h5>
        );
      case 'heading-six':
        return (
          <h6 {...attributes} style={{ ...THUNDERBIRD_HEADING_STYLES['heading-six'], textAlign: element.align }}>
            {children}
          </h6>
        );
      case 'address':
        return (
          <address {...attributes} style={{ ...THUNDERBIRD_ADDRESS_STYLE, textAlign: element.align }}>
            {children}
          </address>
        );
      case 'preformat':
        return (
          <pre
            {...attributes}
            style={{
              ...THUNDERBIRD_PREFORMAT_STYLE,
              textAlign: element.align,
            }}
          >
            {children}
          </pre>
        );
      case 'block-quote':
        return (
          <blockquote
            {...attributes}
            className="email-quoted-block"
            style={{
              margin: '16px 0',
              padding: '12px 16px',
              borderLeft: '4px solid #94a3b8',
              background: 'var(--email-quoted-bg, #f1f5f9)',
              borderRadius: '0 8px 8px 0',
              color: 'inherit',
            }}
          >
            {children}
          </blockquote>
        );
      case 'reply-header-box': {
        const box = element as { type: 'reply-header-box'; style?: string };
        return (
          <div
            {...attributes}
            data-ums-reply-header="true"
            className="email-reply-header-box"
            style={inlineStyleStringToReact(box.style)}
          >
            {children}
          </div>
        );
      }
      case 'quoted-rich-html':
        return (
          <QuotedRichHtmlElement
            attributes={attributes}
            children={children}
            element={element as CustomElement & { type: 'quoted-rich-html'; html: string }}
            editor={editor}
          />
        );
      case 'raw-html': {
        const rawEl = element as { type: 'raw-html'; html: string; inline?: boolean };
        /** Reply/forward metadata header: render in-flow (no iframe) so it cannot stack under the signature. */
        if (rawEl.inline && rawEl.html) {
          return (
            <div
              {...attributes}
              contentEditable={false}
              data-slate-raw-html
              className="slate-raw-html-inline"
              style={{
                display: 'block',
                clear: 'both',
                width: '100%',
                marginTop: 0,
                marginBottom: '0.5rem',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <div
                className="slate-raw-html-inline-inner"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rawEl.html, REPLY_HEADER_PURIFY) }}
              />
              {children}
            </div>
          );
        }
        return (
          <div
            {...attributes}
            contentEditable={false}
            data-slate-raw-html
            style={{
              display: 'block',
              clear: 'both',
              width: '100%',
              marginTop: '0.25rem',
              marginBottom: '0.5rem',
            }}
          >
            <QuotedEmailIframe html={rawEl.html || ''} />
            {children}
          </div>
        );
      }
      case 'bulleted-list':
        return <ul {...attributes}>{children}</ul>;
      case 'numbered-list':
        return <ol {...attributes}>{children}</ol>;
      case 'list-item':
        return <li {...attributes}>{children}</li>;
      case 'table': {
        const path = ReactEditor.findPath(editor, element);
        const handleResizeMouseDown = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const wrapper = (e.target as HTMLElement).closest('.slate-table-wrapper') as HTMLElement | null;
          const rect = wrapper?.getBoundingClientRect();
          const w = rect?.width ?? 400;
          const h = rect?.height ?? 120;
          startTableResize(path, e.clientX, e.clientY, w, h);
        };
        return (
          <div
            className="slate-table-wrapper"
            style={{
              position: 'relative',
              display: 'inline-block',
              width: tableWidth ?? '100%',
              maxWidth: '100%',
              ...(tableHeight ? { height: tableHeight, minHeight: tableHeight } : {}),
            }}
          >
            <table
              {...attributes}
              style={{
                width: '100%',
                height: tableHeight ? '100%' : undefined,
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
              }}
            >
              <tbody>{children}</tbody>
            </table>
            <span
              contentEditable={false}
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: 20,
                height: 20,
                pointerEvents: 'none',
              }}
              aria-hidden
            >
              <span
                role="presentation"
                onMouseDown={handleResizeMouseDown}
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  width: 14,
                  height: 14,
                  cursor: 'se-resize',
                  background: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '2px',
                  pointerEvents: 'auto',
                }}
                title="Drag to resize table (width and height)"
              />
            </span>
          </div>
        );
      }
      case 'table-row': {
        const rowHeight = (element as { height?: string }).height;
        return (
          <tr {...attributes} style={rowHeight ? { height: rowHeight, minHeight: rowHeight } : undefined}>
            {children}
          </tr>
        );
      }
      case 'table-cell': {
        const cellPath = ReactEditor.findPath(editor, element);
        const isFirstCellInRow = cellPath.length >= 2 && cellPath[cellPath.length - 1] === 0;
        const rowPath = cellPath.slice(0, -1);
        const tablePath = cellPath.slice(0, -2) as Path;
        const columnIndex = cellPath[cellPath.length - 1] as number;
        const cellWidth = (element as { width?: string }).width;
        const handleRowResizeMouseDown = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const tr = (e.target as HTMLElement).closest('tr') as HTMLElement | null;
          const h = tr?.getBoundingClientRect().height ?? 32;
          startRowResize(rowPath, e.clientY, h);
        };
        const handleColumnResizeMouseDown = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const td = (e.target as HTMLElement).closest('td');
          const cw = td?.getBoundingClientRect().width ?? 80;
          startColumnResize(tablePath, columnIndex, e.clientX, cw);
        };
        return (
          <td
            {...attributes}
            colSpan={element.colSpan}
            rowSpan={element.rowSpan}
            style={{
              border: '1px solid #e5e7eb',
              padding: 6,
              verticalAlign: 'top',
              position: 'relative',
              minHeight: 28,
              ...(cellWidth ? { width: cellWidth } : {}),
            }}
          >
            {children}
            <span
              contentEditable={false}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: 10,
                pointerEvents: 'none',
              }}
              aria-hidden
            >
              <span
                role="presentation"
                onMouseDown={handleColumnResizeMouseDown}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 6,
                  cursor: 'col-resize',
                  background: 'linear-gradient(to right, transparent 0%, rgba(148, 163, 184, 0.35) 100%)',
                  pointerEvents: 'auto',
                }}
                title="Drag to resize this column (all rows)"
              />
            </span>
            {isFirstCellInRow && (
              <span
                contentEditable={false}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 8,
                  pointerEvents: 'none',
                }}
                aria-hidden
              >
                <span
                  role="presentation"
                  onMouseDown={handleRowResizeMouseDown}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 4,
                    cursor: 'ns-resize',
                    background: 'linear-gradient(to bottom, transparent 0%, #94a3b8 50%)',
                    pointerEvents: 'auto',
                  }}
                  title="Drag to resize this row only"
                />
              </span>
            )}
          </td>
        );
      }
      case 'link':
        return (
          <a {...attributes} href={element.url} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      case 'image': {
        const path = ReactEditor.findPath(editor, element);
        const imgEl = element as { url: string; alt?: string; width?: string; height?: string };
        const imgStyle: React.CSSProperties = {
          display: 'block',
          minHeight: MIN_IMAGE_SIZE_PX,
          maxWidth: '100%',
        };
        if (imgEl.width) imgStyle.width = imgEl.width;
        if (imgEl.height) imgStyle.height = imgEl.height;
        if (!imgEl.width && !imgEl.height) imgStyle.height = 'auto';
        const wrapperWidth = imgEl.width || 'auto';
        const wrapperHeight = imgEl.height || 'auto';
        const handleResizeMouseDown = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const img = (e.target as HTMLElement).closest('span[data-slate-image-wrapper]')?.querySelector('img');
          const rect = img?.getBoundingClientRect();
          const w = rect?.width ?? 200;
          const h = rect?.height ?? 150;
          startImageResize(path, e.clientX, e.clientY, w, h);
        };
        return (
          <span
            {...attributes}
            contentEditable={false}
            data-slate-image-wrapper
            style={{
              display: 'inline-block',
              verticalAlign: 'middle',
              position: 'relative',
              width: wrapperWidth,
              height: wrapperHeight !== 'auto' ? wrapperHeight : undefined,
            }}
          >
            <img src={imgEl.url} alt={imgEl.alt || ''} style={imgStyle} draggable={false} />
            <span
              role="presentation"
              onMouseDown={handleResizeMouseDown}
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: 14,
                height: 14,
                cursor: 'se-resize',
                background: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)',
                border: '1px solid #e2e8f0',
                borderRadius: '2px',
                pointerEvents: 'auto',
              }}
              title="Drag to resize image"
            />
            {children}
          </span>
        );
      }
      default:
        return (
          <p {...attributes} style={{ ...THUNDERBIRD_PARAGRAPH_STYLE, textAlign: element.align }}>
            {children}
          </p>
        );
    }
  }, [editor, startTableResize, startRowResize, startColumnResize, startImageResize]);

  const renderLeaf = useCallback((props: { attributes: any; children: any; leaf: CustomText }) => {
    const { attributes, children, leaf } = props;
    const style: React.CSSProperties = {
      ...((attributes.style as React.CSSProperties | undefined) ?? {}),
    };
    if (leaf.fontFamily) style.fontFamily = leaf.fontFamily;
    if (leaf.fontSize) style.fontSize = leaf.fontSize;
    if (leaf.color) style.color = leaf.color;
    if (leaf.backgroundColor) style.backgroundColor = leaf.backgroundColor;

    let content = children;
    if (leaf.bold) content = <strong>{content}</strong>;
    if (leaf.italic) content = <em>{content}</em>;
    if (leaf.underline) content = <u>{content}</u>;
    if (leaf.strikethrough) content = <s>{content}</s>;

    return (
      <span {...attributes} style={Object.keys(style).length ? style : undefined}>
        {content}
      </span>
    );
  }, []);

  const c = colors;
  const toolbarSurface = c.surface ?? 'bg-white dark:bg-slate-900';
  const useInternalScroll = stickyToolbar && (fillHeight || belowEditor != null || scrollFooter != null);
  const expandUserZone = fillHeight && !compactUserZone;
  const hostFillsParent = expandUserZone || useInternalScroll;
  const editableMinHeight =
    compactUserZone
      ? undefined
      : typeof minHeight === 'number'
        ? `${minHeight}px`
        : minHeight;

  return (
    <div
      ref={editableHostRef}
      className={cn(
        'flex flex-col border rounded-lg overflow-hidden',
        hostFillsParent && 'flex-1 min-h-0',
        c.border,
        className
      )}
    >
      <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
        <SlateEmailToolbar
          config={toolbarConfig}
          onInsertImage={onInsertImage}
          onContentChange={syncHtmlToParent}
          colors={c}
          className={cn(
            'flex-shrink-0 z-30 shadow-sm',
            toolbarSurface,
            stickyToolbar && 'border-b',
          )}
        />
        {useInternalScroll ? (
          <div
            className={cn(
              'email-compose-canvas flex flex-col overflow-y-auto min-h-0',
              EMAIL_COMPOSE_EDITOR_CANVAS_CLASS,
              hostFillsParent && 'flex-1',
            )}
          >
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              placeholder={placeholder}
              onPaste={handlePaste}
              spellCheck
              style={{
                ...(editableMinHeight ? { minHeight: editableMinHeight } : {}),
                padding: 12,
                fontSize: 14,
                fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
              }}
              className={cn('outline-none flex-shrink-0', EMAIL_COMPOSE_EDITOR_CANVAS_CLASS)}
            />
            {belowEditor ? (
              <div className={cn('flex-shrink-0 border-t border-gray-200 dark:border-gray-300', EMAIL_COMPOSE_EDITOR_CANVAS_CLASS)}>
                {belowEditor}
              </div>
            ) : null}
            {scrollFooter ? <div className="flex-shrink-0">{scrollFooter}</div> : null}
          </div>
        ) : (
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            onPaste={handlePaste}
            spellCheck
            style={{
              ...(fillHeight
                ? { flex: 1, minHeight: 0, overflow: 'auto', padding: 12, fontSize: 14 }
                : { minHeight: editableMinHeight, padding: 12, fontSize: 14 }),
              fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
            }}
            className={cn('email-compose-canvas outline-none', EMAIL_COMPOSE_EDITOR_CANVAS_CLASS, fillHeight && 'flex-1 min-h-0')}
          />
        )}
      </Slate>
    </div>
  );
});

export default SlateEmailEditor;
